import React, { Component, FC } from 'react';
import styled from 'styled-components';

import { Screenshot } from 'types/app';
import { DEVICE_PREVIEW_DIMENSIONS } from 'config';

import ImageContainer from 'components/v2/ImageContainer/index';

const DEVICES: any = DEVICE_PREVIEW_DIMENSIONS;
const PREMIUM_BEZEL = 5;

interface DeviceProps {
  deviceName?: string;
  image?: Screenshot;
  preloadDeviceImage?: boolean;
  premiumIcon?: string;
}

interface DefinedDeviceProps extends DeviceProps {
  deviceName: string;
}

interface ContainerProps {
  device: {
    top: number;
    width: number;
    translateLeft?: number;
    frameHeight: number;
    marginTop: number;
  };
}

const getDeviceImageUrl = (deviceName: string) =>
  `/device-images/${deviceName.replace(' ', '_')}.png`;

const StyledImageContainer = styled(ImageContainer)`
  max-width: 200px;
  max-height: 200px;
`;

const BaseFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const DeviceFrame = styled(BaseFrame)<ContainerProps>`
  height: ${({ device: { frameHeight } }) => frameHeight}px;
`;

const DeviceImage = styled.div<DefinedDeviceProps>`
  height: 100%;
  width: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  ${({ deviceName }) => `
    background-image: url(${getDeviceImageUrl(deviceName)});
  `}
`;

const DeviceClockImageContainer = styled.div<ContainerProps>`
  ${({ device: { top, width, translateLeft = 0 } }) => `
    position: absolute;
    left: 50%;
    top: ${top}px;
    width: ${width}px;

    transform: translateX(-${50 + translateLeft}%);
  `}
`;

const HiddenPreloadImage = styled.img`
  position: absolute;
  left: -99999;
  height: 0px;
  width: 0px;

  opacity: 0;
`;

const HiddenPreloadContent = styled.div<any>`
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
`;

const PremiumImageContainerStyle = (premiumIcon: any) => `
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${PREMIUM_BEZEL}px;
  ${premiumIcon ?
    `background: url(${premiumIcon}) center center / contain no-repeat;` :
    ''
    }
`;

class PreloadDeviceImage extends Component<DefinedDeviceProps> {
  state = {
    loaded: false
  };

  handleImageLoaded = () =>
    this.setState({
      loaded: true
    });

  render() {
    const { deviceName, children } = this.props;
    const { loaded } = this.state;

    return (
      <div>
        <HiddenPreloadImage
          src={getDeviceImageUrl(deviceName)}
          onLoad={this.handleImageLoaded}
          alt=''
        />
        <HiddenPreloadContent loaded={loaded}>{children}</HiddenPreloadContent>
      </div>
    );
  }
}

const Device: FC<DeviceProps> = ({
  deviceName,
  image,
  children,
  preloadDeviceImage = true,
  premiumIcon,
}) => {
  const device = deviceName && DEVICES[deviceName];

  const imageContainer = image && (
    <StyledImageContainer
      containerAttr={{
        width: image.width,
        height: image.height,
        iconPlaceholder: false,
        styles: premiumIcon ? PremiumImageContainerStyle(premiumIcon) : null,
      }}
      imageAttr={{
        alt: deviceName,
        src: image.uri,
        styles: !device && {
          'max-height': '200px'
        }
      }}
      borderRadius={(device && device.borderRadius) != null ? `${device.borderRadius}px` : null}
    />
  );

  if (!deviceName || !device) {
    return (
      <BaseFrame>
        {imageContainer}
        {children}
      </BaseFrame>
    );
  }

  const frame = (
    <DeviceFrame device={device}>
      <DeviceImage deviceName={deviceName} />
      <DeviceClockImageContainer device={device}>
        {imageContainer}
        {children}
      </DeviceClockImageContainer>
    </DeviceFrame>
  );

  if (preloadDeviceImage) {
    return (
      <PreloadDeviceImage deviceName={deviceName}>
        {frame}
      </PreloadDeviceImage>
    );
  }

  return frame;
};

export default Device;
